/**
 * Provider-agnostic auth hooks.
 *
 * All components import from here instead of directly from @frontegg/react.
 * This is the single abstraction layer for auth — when migrating to Keycloak
 * (or any other provider), only this file changes. The 15+ consumer files
 * stay untouched.
 *
 * Current provider chain:
 *   1. Frontegg (primary — works on main domain and Chrome custom domains)
 *   2. AuthService handoff JWT (fallback — Safari custom domains where
 *      Frontegg can't maintain cookies due to ITP)
 *   3. Future: Keycloak (per-tenant, replaces both above for custom domains)
 */
import { useAuth as useFronteggAuth, useAuthActions as useFronteggAuthActions, useTenantsState as useFronteggTenantsState } from '@frontegg/react';
import AuthService from '../services/auth.service';

/** Normalized user shape — provider-agnostic */
export interface AppUser {
  id: string;
  sub: string;
  name: string;
  email: string;
  tenantId: string;
  tenantIds: string[];
  roles: Array<{ key: string; name: string }>;
  accessToken: string;
  profilePictureUrl: string;
  verified: boolean;
  mfaEnrolled: boolean;
  activatedForTenant: boolean;
  tenants: Array<{ tenantId: string; name: string }>;
  customClaims: Record<string, any>;
  permissions: string[];
}

export interface AppAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AppUser | undefined;
}

export interface AppAuthActions {
  logout: () => void;
  switchTenant: (params: { tenantId: string }) => void;
}

export interface AppTenantsState {
  tenants: Array<{ tenantId: string; name: string }>;
  loading: boolean;
}

function buildUserFromAuthState(authState: NonNullable<ReturnType<AuthService['getAuthState']>>): AppUser {
  return {
    id: authState.sub,
    sub: authState.sub,
    name: authState.name,
    email: authState.email,
    tenantId: authState.tenant_id,
    tenantIds: [authState.tenant_id],
    roles: (authState.roles || []).map(r => ({ key: r, name: r })),
    accessToken: authState.access_token,
    profilePictureUrl: authState.profile_img_url || '',
    verified: true,
    mfaEnrolled: false,
    activatedForTenant: true,
    tenants: authState.tenants || [],
    customClaims: authState.customClaims || {},
    permissions: [],
  };
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useAppAuth(): AppAuthState {
  const frontegg = useFronteggAuth();
  const authService = AuthService.getInstance();
  const authState = authService.getAuthState();

  // Provider 1: Frontegg
  if (frontegg.isAuthenticated && frontegg.user) {
    return frontegg as any;
  }

  // Provider 2: Handoff JWT (Safari custom domain fallback)
  // Future: Provider 3 (Keycloak) would go here, checked before handoff
  if (authState && authService.handoffToken) {
    return {
      isAuthenticated: true,
      isLoading: false,
      user: buildUserFromAuthState(authState),
    };
  }

  return frontegg as any;
}

export function useAppAuthActions(): AppAuthActions {
  const fronteggActions = useFronteggAuthActions();
  const authService = AuthService.getInstance();

  // Custom domain handoff: wrap logout to clean up local state
  // Future: Keycloak logout would be handled here
  if (authService.handoffToken && authService.isCustomDomain) {
    return {
      logout: () => {
        sessionStorage.removeItem('plb_auth_state');
        sessionStorage.removeItem('plb_handoff_token');
        // Keep plb_sso_attempted so the AuthService constructor doesn't
        // auto-redirect to sso-bounce on the next page load. Without this,
        // the IdP (e.g. Okta) still has an active session and auto-authenticates,
        // making it impossible to log out.
        authService.handoffToken = null;
        authService.plbAuthState = null;
        try { fronteggActions.logout(); } catch (e) { /* no valid session */ }

        // Don't redirect to ssoLogoutUrl here — the Sidebar handles IdP
        // logout via a hidden iframe before calling this function.
        window.location.replace('/login');
      },
      switchTenant: fronteggActions.switchTenant,
    };
  }

  return fronteggActions;
}

export function useAppTenantsState(): AppTenantsState {
  const frontegg = useFronteggTenantsState();
  const authService = AuthService.getInstance();
  const authState = authService.getAuthState();

  // Frontegg has data
  if (frontegg.tenants && frontegg.tenants.length > 0) {
    return frontegg as any;
  }

  // Fallback from auth state
  // Future: Keycloak tenant list would go here
  if (authState && authService.handoffToken) {
    return {
      tenants: authState.tenants?.length ? authState.tenants : [{
        tenantId: authState.tenant_id,
        name: authState.tenant_name || authState.tenant_id,
      }],
      loading: false,
    };
  }

  return frontegg as any;
}
